import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './Login.css';

function Login() {
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [showPw, setShowPw]     = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post('/api/auth/login', formData);
      const { token, user } = response.data;
      setAccessToken(token);
      localStorage.setItem('hasSession', 'true');
      localStorage.setItem('userType', user.type);
      localStorage.setItem('username', user.username);
      if (user.isAdmin) localStorage.setItem('isAdmin', 'true');

      // Admin/company/author/editor always go to their own admin page regardless of next param.
      // Only regular readers respect the next param (e.g. redirected from a paywalled article).
      if (user.isAdmin) {
        navigate('/admin');
      } else if (user.type === 'company') {
        localStorage.setItem('companyName', user.company_name);
        navigate(`/adminHUD/${encodeURIComponent(user.company_name)}`);
      } else if (user.type === 'author') {
        localStorage.setItem('companyName', user.company_name);
        localStorage.setItem('displayName', user.display_name || user.username);
        navigate(`/${encodeURIComponent(user.company_name)}/author/${encodeURIComponent(user.username)}`);
      } else if (user.type === 'editor') {
        localStorage.setItem('companyName', user.company_name);
        localStorage.setItem('displayName', user.display_name || user.username);
        navigate(`/${encodeURIComponent(user.company_name)}/editor/${encodeURIComponent(user.username)}`);
      } else {
        const nextParam = new URLSearchParams(window.location.search).get('next');
        navigate(nextParam || '/');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Helmet>
      <title>Log In — PubSilo</title>
      <meta name="robots" content="noindex" />
    </Helmet>
    <div className="login-container">
      <div className="login-form-wrapper">
        <img src="/pubsilo.png" alt="PubSilo" className="login-logo" />
        <h1>Log In to PubSilo</h1>
        <p className="subtitle">Welcome back</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPw ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                placeholder="Enter password"
                style={{ paddingRight: '2.5rem', width: '100%' }}
              />
              <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '0.65rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#999', display: 'flex', alignItems: 'center' }}>
                {showPw
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
          </div>

          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <p className="signup-prompt">
          <Link to="/forgot-password" style={{ fontSize: '0.88rem', color: '#666' }}>Forgot your password?</Link>
        </p>
        <p className="signup-prompt">
          Don't have an account? <Link to="/signup/role">Sign up</Link>
        </p>
      </div>
    </div>
    </>
  );
}

export default Login;
